/**
 * 侧栏 footer 打卡容器 — 接全部数据 hook(状态轮询/开放 session/动作管线/
 * 秒级计时/work type 目录),按 collapsed 渲染展开态 ClockActionRow 或
 * 收起态 RailClockButton(hover 飞出 FlyoutCard)。
 * Plan: docs/superpowers/plans/2026-06-12-clock-panel-handoff-v2.md Task 7
 * ⋯ more actions:My Shift 弹窗(恒显示,2026-06-12 起菜单树不再含 /my-shifts,门控随导航清理移除) + Timesheet/Daily Work 弹当前用户 AttendanceDetail(portal)。
 * Plan: docs/superpowers/plans/2026-06-12-clock-panel-more-actions.md Task 4
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { refreshCoordinator } from '@/features/realtime/refreshCoordinator';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAppSelector } from "@/store/hooks";
import { usePermission } from "@/hooks/usePermission";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { asSnowflakeId } from "@/types/snowflake";
import { openReportIssue } from "@/config/reportIssue";
import type { AttendanceRecord, AttendanceSessionRef } from "@/types/attendance";
import { MyShiftDialog } from "@/features/my-shift";
import { ClockActionRow } from "./ClockActionRow";
import { CrossDayPrompt } from "./CrossDayPrompt";
import { RailClockButton } from "./RailClockButton";
import {
  BREAK_TYPE_OPTIONS,
  deriveClockUiState,
  deriveDisplayMode,
  sinceLabel,
} from "./derive";
import {
  computeDefaultClockOut,
  computeResolveTimes,
  deriveCrossDayState,
  isStaleEntry,
} from "./crossDay";
import { useClockActions } from "./useClockActions";
import { useClockTicker } from "./useClockTicker";
import { useMyClockStatus } from "./useMyClockStatus";
import { useOpenSessions } from "./useOpenSessions";
import { serverNowMs } from "@/utils/serverClock";
import { todayBizDate, useCrossDayCheck } from "./useCrossDayCheck";
import { useWorkTypeOptions } from "./useWorkTypeOptions";
import type { MoreActionsConfig } from "./ClockMoreMenu";

// 自助 Timesheet/Daily Work 弹层(AttendanceDetail → TaskDetail → AI 校验 …)只在点开后渲染,
// 按需加载:侧栏打卡 footer 在每个页面都挂着,静态 import 会把整套详情代码打进入口 chunk。
const AttendanceDetail = lazy(() =>
  import("@/components/staff-attendance/AttendanceDetail").then((m) => ({ default: m.AttendanceDetail })),
);

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Web 打卡入口权限码 — 对齐后端 route 真实校验码(`attendance_controller` clock-in/out/break
 * 全部用 `pms:attendance:update`)。
 *
 * ⚠️ 历史坑(2026-06-20 修):原值是纯前端 advisory 码 `pms:attendance:clock:web`(capability
 * `attendance.web-clock`)。该 capability 已于 2026-06-17 capability 重构(decision B)废弃删除 →
 * 该码不再授予任何角色、也不在 sys.permission 中。结果非 superuser 用户即便持有真实打卡权
 * `pms:attendance:update`,前端 gate 仍恒 false → 按钮变灰打不了卡;唯独 superuser 因
 * usePermission 短路(`if (isSuperuser) return true`)能打卡,造成"只有 superuser 能 web 打卡"的
 * 错误行为。前端 gate 必须与后端 route decorator 同码。
 */
export const WEB_CLOCK_PERM = "pms:attendance:update";

/**
 * 最后一条 break_start.notes → 展示用 break 类型名(T6 审查 I-1):
 * 命中 BREAK_TYPE_OPTIONS 用其 label("lunch"→"Lunch");未知值首字母大写;
 * 无 break_start 或 notes 为空 → null(FlyoutCard sub 行省略类型名)。
 */
export function deriveBreakTypeLabel(
  sessions: AttendanceSessionRef[],
): string | null {
  for (let i = sessions.length - 1; i >= 0; i--) {
    if (sessions[i].sessionType !== "break_start") continue;
    const notes = sessions[i].notes?.trim();
    if (!notes) return null;
    const opt = BREAK_TYPE_OPTIONS.find((o) => o.id === notes);
    if (opt) return opt.label;
    return notes.charAt(0).toUpperCase() + notes.slice(1);
  }
  return null;
}


export function SidebarClockFooter({ collapsed }: { collapsed: boolean }) {
  const { userId, fullName, avatarUrl } = useAppSelector((s) => s.user);
  const sid = userId ? asSnowflakeId(userId) : null;
  const { hasPermission } = usePermission();
  const actionsAllowed = hasPermission(WEB_CLOCK_PERM);

  const { entry, sessions, loaded, refresh: refreshStatus } = useMyClockStatus(sid);
  const { openSessions, loaded: sessionsLoaded, refresh: refreshSessions } =
    useOpenSessions(sid);
  const userTimezone = useUserTimezone();
  const { record: crossDayRecord, refresh: refreshCrossDay } = useCrossDayCheck(
    sid,
    userTimezone,
  );

  // 规则1:陈旧(attendanceDate < 今天业务日)today-entry 当 idle,止幻影计时器
  const bizDate = todayBizDate(userTimezone);
  const previousDate = useRef(bizDate);
  useEffect(() => {
    if (previousDate.current !== bizDate) {
      previousDate.current = bizDate;
      refreshCoordinator.invalidate(['myAttendance']);
    }
  }, [bizDate]);
  const effectiveEntry = isStaleEntry(entry?.attendanceDate, bizDate) ? null : entry;

  // 首次查询开放 session；后续由公共 myAttendance 通知与动作后的 refreshAll 刷新。
  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  const refreshAll = useCallback(
    () => Promise.all([refreshStatus(), refreshSessions(), refreshCrossDay()]),
    [refreshStatus, refreshSessions, refreshCrossDay],
  );
  const actions = useClockActions({ userId: sid, refreshAll });
  const ui = deriveClockUiState(effectiveEntry, openSessions);
  // 时序竞态防护:首拉(loaded)未完成前,entry=null 不可被当成"未打卡"渲染绿色可点按钮——
  // 实际在 working/in_break 的用户误点 clock-in 会触发后端 in_break 守卫缺口、写脏状态。
  // 强制 loading 态(只读、无任何打卡按钮),等真实状态落地再放开。后续 60s 轮询不再翻 loaded,无闪烁。
  // openSessions 同理并入:它未首拉完时 openSessions 恒 [],会被 deriveCrossDayState /
  // deriveDisplayMode 误判成"没有运行中的 special"。
  const ready = loaded && sessionsLoaded;
  const displayMode = ready ? deriveDisplayMode(ui) : "loading";

  // ⚠️ 必须等 sessionsLoaded:openSessions 决定 crossDay.openSpecialTaskId,而它又决定
  // 补签退的默认时刻(有 open special → 顶到 now)。慢网下 sessions 后到就会先渲染一个
  // 默认时刻 = clock_in 的 prompt,用户照着点下去整段班工时清零(2026-07-29 prod 事故)。
  const crossDay = ready ? deriveCrossDayState(crossDayRecord, openSessions) : null;

  // Refresh the suggested time with authoritative state. The form owns edited
  // values and is keyed by attendance identity, so a refresh cannot reset it.
  const crossDayDefaultIso = useMemo(
    () =>
      crossDayRecord
        ? computeDefaultClockOut(
            crossDayRecord,
            // open special 将在 resolve 时被 finish 到 now → 默认时刻顶到 now，
            // 否则整段仍在跑的班会被补签回 clock_in（工时清零）。
            crossDay?.openSpecialTaskId != null ? serverNowMs() : null,
          )
        : null,
    [crossDayRecord, crossDay?.openSpecialTaskId],
  );

  const { resolveCrossDay } = actions;
  const handleResolveCrossDay = useCallback(
    async (targetIso: string) => {
      if (!crossDay || !crossDayRecord) return;
      const { endBreakIso, clockOutIso } = computeResolveTimes(
        crossDay.status, targetIso, crossDayRecord, userTimezone,
      );
      await resolveCrossDay({
        openSpecialTaskId: crossDay.openSpecialTaskId,
        endBreakIso,
        clockOutIso,
      });
      await Promise.all([refreshStatus(), refreshSessions(), refreshCrossDay()]);
    },
    [crossDay, crossDayRecord, userTimezone, resolveCrossDay, refreshStatus, refreshSessions, refreshCrossDay],
  );

  const tick = useClockTicker(sessions, ui.runningSpecial?.startedAt ?? null);
  const { options, loading } = useWorkTypeOptions(actionsAllowed ? sid : null);

  // ── ⋯ more actions:My Shift 弹窗 / Timesheet·Daily Work 弹自助 flyout ──
  const [myShiftOpen, setMyShiftOpen] = useState(false);
  const [selfView, setSelfView] = useState<"timesheet" | "daily" | null>(null);
  // 打开时刻冻结日期,避免跨午夜时 render 漂移
  const [selfDates, setSelfDates] = useState<{
    date: Date;
    range: { start: Date; end: Date };
  } | null>(null);

  const openSelf = useCallback(
    (mode: "timesheet" | "daily") => {
      const now = dayjs().tz(userTimezone);
      setSelfDates({
        // 中午锚点写法对齐 staff-attendance.tsx,规避 DST 边界
        date: now.hour(12).minute(0).second(0).toDate(),
        range: {
          start: now.startOf("month").hour(12).minute(0).second(0).toDate(),
          end: now.endOf("month").hour(12).minute(0).second(0).toDate(),
        },
      });
      setSelfView(mode);
    },
    [userTimezone],
  );

  const moreActions = useMemo<MoreActionsConfig | null>(() => {
    if (!sid) return null;
    return {
      onMyShift: () => setMyShiftOpen(true),
      onTimesheet: () => openSelf("timesheet"),
      onDailyWork: () => openSelf("daily"),
      // 跳出去到 Nova UAT Platform 的上报页,带上当前用户 id。
      // 用原始的 userId 而不是 sid:那边把它当不透明字符串存,
      // 我们没有理由先把它塞进 SnowflakeId 这个本地类型再传出去。
      onReportBug: () => openReportIssue({ userId, name: fullName }),
    };
  }, [sid, openSelf, userId, fullName]);

  // AttendanceDetail 只要 userId 即自取数;fullName/avatarUrl 仅供头部展示
  const selfRecord = useMemo<AttendanceRecord | null>(
    () =>
      sid
        ? {
            id: sid,
            userId: sid,
            fullName: fullName ?? undefined,
            avatarUrl: avatarUrl ?? undefined,
          }
        : null,
    [sid, fullName, avatarUrl],
  );

  const closeSelf = useCallback(() => {
    setSelfView(null);
    setSelfDates(null);
  }, []);

  // ClockActionRow / RailClockButton 同构 props(Flyout 专属的 sinceText /
  // breakTypeLabel 只走 RailClockButton 透传)
  const shared = {
    displayMode,
    tick,
    activityName: ui.runningSpecial?.title ?? null,
    actionsAllowed,
    pending: actions.pending,
    workTypeOptions: options,
    workTypeLoading: loading,
    onClockIn: actions.clockIn,
    onClockOut: actions.clockOut,
    onStartBreak: actions.startBreak,
    onEndBreak: actions.endBreak,
    onStartWorkType: actions.startWorkType,
    onEndWorkType: () => {
      if (ui.runningSpecial) void actions.endWorkType(ui.runningSpecial.taskId);
    },
    moreActions,
  };

  return (
    <>
      {crossDay ? (
        <CrossDayPrompt
          key={crossDay.attendanceId}
          collapsed={collapsed}
          attendanceDate={crossDay.attendanceDate}
          detected={crossDay.detected}
          defaultClockOutIso={crossDayDefaultIso!}
          userTz={userTimezone}
          onResolve={handleResolveCrossDay}
        />
      ) : collapsed ? (
        <RailClockButton
          {...shared}
          sinceText={sinceLabel(effectiveEntry?.firstClockInAt)}
          breakTypeLabel={deriveBreakTypeLabel(effectiveEntry ? sessions : [])}
        />
      ) : (
        <ClockActionRow {...shared} />
      )}
      {selfView && selfDates && selfRecord && (
        <Suspense fallback={null}>
          <AttendanceDetail
            record={selfRecord}
            viewMode={selfView}
            date={selfDates.date}
            dateRange={selfDates.range}
            isOpen
            onClose={closeSelf}
            selfServiceUserId={sid}
          />
        </Suspense>
      )}
      {/* 受控弹窗:open=false 时 Radix 不挂载内容,MyShiftPanel 数据 hook 不会空跑 */}
      <MyShiftDialog open={myShiftOpen} onOpenChange={setMyShiftOpen} />
    </>
  );
}
